import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Users, GraduationCap, Rocket, Play } from "lucide-react";
import { IntroVideoModal } from "@/components/IntroVideoModal";

const HERO_IMG = "https://images.unsplash.com/photo-1595232928465-856bef2ff8d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const pillars = [
  { icon: Users, t: "Connect", d: "Meet a global network of leaders and experts across AI, Technology and Engineering." },
  { icon: GraduationCap, t: "Learn", d: "Share knowledge and real-world experience through executive courses and firesides." },
  { icon: Rocket, t: "Build", d: "Turn ideas into meaningful impact alongside people building at the frontier." },
];

const topics = ["Artificial Intelligence", "Tech Leadership", "Engineering Culture", "Future of Work", "Product Development"];

export default function Landing() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/feed", { replace: true });
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex items-center justify-between px-5 sm:px-10 h-16 border-b border-border bg-paper/80 backdrop-blur-md sticky top-0 z-30">
        <div className="flex items-center gap-2.5">
          <img src="/ikigora-icon.png" alt="Ikigora" className="w-9 h-9 object-contain" />
          <span className="font-serif text-xl font-extrabold tracking-tight">Ikigora</span>
        </div>
        <button onClick={login} data-testid="landing-signin-button"
          className="px-5 py-2 text-sm font-medium bg-cobalt text-white rounded-md hover:bg-gold-hover transition-colors">
          Sign in
        </button>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-10 pt-14 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-cobalt fade-up">
            People · Ideas · Opportunities
          </div>
          <h1 className="mt-6 font-serif font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight fade-up" style={{ animationDelay: "0.05s" }}>
            Bold ideas.<br /><span className="text-cobalt">Brighter futures.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-md leading-relaxed fade-up" style={{ animationDelay: "0.12s" }}>
            A global community for the builders, leaders and doers in AI, Technology and Engineering — sharing knowledge, courses and real-world experience.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4 fade-up" style={{ animationDelay: "0.2s" }}>
            <button onClick={login} data-testid="hero-google-login-button"
              className="group inline-flex items-center gap-3 bg-cobalt text-white px-6 py-3.5 text-sm font-semibold rounded-md hover:bg-gold-hover transition-colors">
              <GoogleGlyph /> Join the conversation
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => setShowIntro(true)} data-testid="watch-video-button"
              className="group inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <span className="grid place-items-center w-8 h-8 rounded-full border border-border group-hover:border-cobalt group-hover:text-cobalt transition-colors"><Play size={13} /></span>
              Watch video
            </button>
          </div>
          <div className="mt-8 flex flex-wrap gap-2 fade-up" style={{ animationDelay: "0.26s" }}>
            {topics.map((t) => (
              <span key={t} className="text-xs text-muted-foreground border border-border rounded-full px-3 py-1">#{t}</span>
            ))}
          </div>
        </div>

        <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative rounded-2xl overflow-hidden border border-border shadow-xl">
            <img src={HERO_IMG} alt="" className="w-full h-[440px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-navy/80 via-navy/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-7 text-white">
              <div className="text-2xl sm:text-3xl font-serif font-bold leading-tight">A stronger tomorrow,<br />built together.</div>
              <div className="mt-2 eyebrow text-white/70">Ideas. People. Progress.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="brand-gradient text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-10 py-16 grid sm:grid-cols-3 gap-10">
          {pillars.map((p, i) => (
            <div key={p.t} className="fade-up" style={{ animationDelay: `${0.1 + i * 0.08}s` }}>
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-white/10 border border-white/15">
                <p.icon size={22} />
              </span>
              <h3 className="mt-5 font-serif text-2xl font-bold">{p.t}</h3>
              <p className="mt-2 text-sm text-white/70 leading-relaxed max-w-xs">{p.d}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-5 sm:px-10 py-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 eyebrow text-white/50">
            <span>People</span><span className="text-cobalt">·</span><span>Ideas</span>
            <span className="text-cobalt">·</span><span>Opportunities</span><span className="text-cobalt">·</span><span>A Brighter Tomorrow</span>
          </div>
        </div>
      </section>

      <IntroVideoModal open={showIntro} onClose={() => setShowIntro(false)} onJoin={login} />
    </div>
  );
}

const GoogleGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 4.7 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 4.7 29.5 3 24 3 16 3 9.1 7.6 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 45c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 36 26.7 37 24 37c-5.3 0-9.7-2.6-11.3-7l-6.6 5.1C9 40.4 15.9 45 24 45z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.3 5.4l6.3 5.3C40.9 36.3 45 30.9 45 24c0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);
